import React, { useState, useEffect, useCallback } from 'react';
import { getBackendUrl } from '../utils/api';
import storage from '../utils/storage';
import { AlertCircle, CheckCircle, Play, Eye, RefreshCw, Database, Merge, Shield, TrendingUp } from 'lucide-react';

const BACKEND_URL = getBackendUrl();

/**
 * Canonical Merge Plays (Historical) - Admin Job Component
 * 
 * Purpose: Safely merges duplicate play records that share the same
 * song_uid, station_id, and date_bucket.
 * 
 * Safety Guarantees:
 * - No loss of plays
 * - No loss of station attribution
 * - Charts totals before vs after must match
 */
const CanonicalMergeJob = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mergeStatus, setMergeStatus] = useState(null);
  const [auditResult, setAuditResult] = useState(null);
  const [mergeResult, setMergeResult] = useState(null);
  const [integrityScore, setIntegrityScore] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Fetch current merge status
  const fetchMergeStatus = useCallback(async () => {
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/radio-chart/admin/merge-status?t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          setMergeStatus(data.status);
        } catch (parseErr) {
          console.error('Failed to parse merge status JSON:', parseErr);
        }
      } else {
        console.error('Merge status response not OK:', response.status);
      }
    } catch (err) {
      console.error('Error fetching merge status:', err);
      setError(`Merge status error: ${err.message}`);
    }
  }, []);

  // Fetch data integrity score
  const fetchIntegrityScore = useCallback(async () => {
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/radio-chart/admin/data-integrity-score?t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        // Read JSON directly - don't try to clone or read multiple times
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          setIntegrityScore(data.integrity);
        } catch (parseErr) {
          console.error('Failed to parse integrity score JSON:', parseErr);
          setError('Failed to parse integrity data');
        }
      } else {
        const errorText = await response.text();
        console.error('Integrity score response not OK:', response.status, errorText);
        setError(`Integrity score error: HTTP ${response.status}`);
      }
    } catch (err) {
      console.error('Error fetching integrity score:', err);
      setError(`Integrity score error: ${err.message}`);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchMergeStatus();
    fetchIntegrityScore();
    setLastRefresh(new Date());
  }, [fetchMergeStatus, fetchIntegrityScore]);

  // Preview (Dry Run)
  const handlePreview = async () => {
    setPreviewLoading(true);
    setError(null);
    setAuditResult(null);
    
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/radio-chart/admin/duplicate-audit?t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      // Read response text once
      const text = await response.text();
      
      if (!response.ok) {
        throw new Error(`Audit failed (${response.status}): ${text}`);
      }
      
      try {
        const data = JSON.parse(text);
        setAuditResult(data.audit);
      } catch (parseErr) {
        throw new Error(`Failed to parse audit response: ${parseErr.message}`);
      }
    } catch (err) {
      console.error('Preview error:', err);
      setError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Execute Merge
  const handleExecuteMerge = async () => {
    if (!window.confirm(
      '⚠️ ΠΡΟΣΟΧΗ: Αυτή η ενέργεια θα συγχωνεύσει διπλότυπα records.\n\n' +
      'Θα διατηρηθούν ΟΛΑ τα plays και TLH.\n' +
      'Τα διπλότυπα θα επισημανθούν ως merged.\n\n' +
      'Είστε σίγουροι;'
    )) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setMergeResult(null);
    
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/radio-chart/admin/duplicate-merge?dry_run=false&t=${Date.now()}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      // Read response text once
      const text = await response.text();
      
      if (!response.ok) {
        throw new Error(`Merge failed (${response.status}): ${text}`);
      }
      
      try {
        const data = JSON.parse(text);
        setMergeResult(data.result);
      } catch (parseErr) {
        throw new Error(`Failed to parse merge response: ${parseErr.message}`);
      }
      
      // Refresh status after a short delay
      setTimeout(async () => {
        await fetchMergeStatus();
        await fetchIntegrityScore();
        setLastRefresh(new Date());
      }, 1000);
      
    } catch (err) {
      console.error('Execute merge error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Refresh all data
  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchMergeStatus();
      await fetchIntegrityScore();
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Refresh error:', err);
      setError(`Refresh failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Merge className="w-7 h-7 text-purple-600" />
            Canonical Merge Plays (Historical)
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Συγχώνευση διπλότυπων play records χωρίς απώλεια δεδομένων
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Data Integrity Score Card */}
      {integrityScore && (
        <div className={`rounded-xl p-6 ${
          integrityScore.data_integrity_score === 100 
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
            : integrityScore.data_integrity_score >= 80
            ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className={`w-8 h-8 ${
                integrityScore.data_integrity_score === 100 ? 'text-green-600' :
                integrityScore.data_integrity_score >= 80 ? 'text-yellow-600' : 'text-red-600'
              }`} />
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Data Integrity Score</div>
                <div className="text-3xl font-bold flex items-center gap-2">
                  <span className={
                    integrityScore.data_integrity_score === 100 ? 'text-green-600' :
                    integrityScore.data_integrity_score >= 80 ? 'text-yellow-600' : 'text-red-600'
                  }>
                    {integrityScore.data_integrity_score}%
                  </span>
                  <span className="text-lg">{integrityScore.status_emoji}</span>
                  <span className="text-sm font-normal text-gray-500">{integrityScore.status}</span>
                </div>
              </div>
            </div>
            <div className="text-right text-sm text-gray-500 dark:text-gray-400">
              <div>Duplicates: {integrityScore.conditions?.duplicate_count || 0}</div>
              <div>Unresolved: {integrityScore.conditions?.unresolved_song_id_count || 0}</div>
              <div>Violations: {integrityScore.conditions?.invariant_violations || 0}</div>
            </div>
          </div>
        </div>
      )}

      {/* Current Status Card */}
      {mergeStatus && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            Τρέχουσα Κατάσταση
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {mergeStatus.total_records?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Σύνολο Records</div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">
                {mergeStatus.active_records?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Active (Canonical)</div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-600">
                {mergeStatus.merged_records?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Merged (Hidden)</div>
            </div>
            
            <div className={`rounded-lg p-4 ${
              mergeStatus.remaining_duplicate_groups === 0 
                ? 'bg-green-50 dark:bg-green-900/30' 
                : 'bg-red-50 dark:bg-red-900/30'
            }`}>
              <div className={`text-2xl font-bold ${
                mergeStatus.remaining_duplicate_groups === 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {mergeStatus.remaining_duplicate_groups || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Remaining Duplicates</div>
            </div>
          </div>
          
          {lastRefresh && (
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              Last updated: {lastRefresh.toLocaleTimeString('el-GR')}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4">
        <button
          onClick={handlePreview}
          disabled={previewLoading || loading}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="canonical-merge-preview-btn"
        >
          <Eye className="w-5 h-5" />
          {previewLoading ? 'Scanning...' : 'Preview (Dry Run)'}
        </button>
        
        <button
          onClick={handleExecuteMerge}
          disabled={loading || previewLoading}
          className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="canonical-merge-execute-btn"
        >
          <Play className="w-5 h-5" />
          {loading ? 'Executing...' : 'Execute Merge'}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800 dark:text-red-200">Error</div>
            <div className="text-red-700 dark:text-red-300">{error}</div>
          </div>
        </div>
      )}

      {/* Preview Results */}
      {auditResult && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            Preview Results
          </h3>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">
                {auditResult.duplicate_groups || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Duplicate Groups</div>
            </div>
            
            <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-orange-600">
                {auditResult.total_rows_affected || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Rows Affected</div>
            </div>
            
            <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">
                {auditResult.rows_to_merge || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Rows to Merge</div>
            </div>
          </div>
          
          {/* Sample Groups */}
          {auditResult.sample_groups && auditResult.sample_groups.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Sample Duplicate Groups (first 10):
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left">Song UID</th>
                      <th className="px-3 py-2 text-left">Station</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-center">Records</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {auditResult.sample_groups.map((group, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-3 py-2 font-mono text-xs">{group.song_uid?.substring(0, 15)}...</td>
                        <td className="px-3 py-2">{group.station_id}</td>
                        <td className="px-3 py-2">{group.day}</td>
                        <td className="px-3 py-2 text-center">
                          <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-xs font-medium">
                            {group.record_count}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {auditResult.duplicate_groups === 0 && (
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <span className="text-green-800 dark:text-green-200 font-medium">
                Δεν βρέθηκαν διπλότυπα! Το σύστημα είναι καθαρό.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Merge Results */}
      {mergeResult && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            {mergeResult.status === 'completed' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
            Merge Results
          </h3>
          
          {/* Summary */}
          <div className={`p-4 rounded-lg mb-4 ${
            mergeResult.status === 'completed' 
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
          }`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {mergeResult.steps?.audit?.duplicate_groups || 0}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Duplicates Before</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">0</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Duplicates After</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {mergeResult.steps?.merge?.rows_marked_merged || 0}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Rows Merged</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">0</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Plays Delta</div>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
                mergeResult.status === 'completed'
                  ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
                  : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200'
              }`}>
                {mergeResult.status === 'completed' ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    SUCCESS
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4" />
                    {mergeResult.status?.toUpperCase()}
                  </>
                )}
              </span>
            </div>
          </div>
          
          {/* Validation Results */}
          {mergeResult.steps?.validation && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Validation Results:
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  {mergeResult.steps.validation.plays_match ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span>Plays Match: {mergeResult.steps.validation.plays_match ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {mergeResult.steps.validation.tlh_match ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span>TLH Match: {mergeResult.steps.validation.tlh_match ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {mergeResult.steps.validation.no_data_loss ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span>No Data Loss: {mergeResult.steps.validation.no_data_loss ? 'Confirmed' : 'Warning'}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Timestamps */}
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 flex justify-between">
            <span>Started: {mergeResult.started_at ? new Date(mergeResult.started_at).toLocaleString('el-GR') : 'N/A'}</span>
            <span>Completed: {mergeResult.completed_at ? new Date(mergeResult.completed_at).toLocaleString('el-GR') : 'N/A'}</span>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">ℹ️ Πληροφορίες</h4>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Τα διπλότυπα ανιχνεύονται με βάση: <code className="bg-blue-100 dark:bg-blue-900/40 px-1 rounded">song_uid + station_id + date</code></li>
          <li>• Τα plays και TLH αθροίζονται στο canonical record</li>
          <li>• Τα παλιά records επισημαίνονται ως <code className="bg-blue-100 dark:bg-blue-900/40 px-1 rounded">merged=true</code>, δεν διαγράφονται</li>
          <li>• Τα charts, People's Chart, και EHP ενημερώνονται αυτόματα</li>
          <li>• Το job είναι idempotent - ασφαλές να τρέξει πολλές φορές</li>
        </ul>
      </div>
    </div>
  );
};

export default CanonicalMergeJob;
